import { useMutation } from "@tanstack/react-query"
import { Calculator, Plus, Trash2 } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { paraElServidor as aDecimal, normalizarDecimal } from "@/lib/decimales"
import type { BorradorRetencion, Calculo, LineaRetencion } from "./tipos"
import { lineaEnBlanco } from "./tipos"

type DesdeNeto = { base: string; exacto: boolean; calculo: Calculo }

/** Las tres retenciones que se marcan por línea, con su rótulo corto y su ayuda. */
const MARCAS = [
  ["exento_iva", "Exento IVA", "El documento lo marca «(E)»: no lleva IVA"],
  ["retiene_iva", "Ret. IVA", "Se le retiene el IVA de este servicio"],
  ["retiene_islr", "Ret. ISLR", "Se le retiene ISLR sobre este servicio"],
] as const

/**
 * Los servicios del documento y qué retención lleva cada uno.
 *
 * ⚠ **Se marca por LÍNEA**: el almacenaje va exento de IVA y sí retiene ISLR, en el
 * mismo documento que el manejo gravado (ver `models/retenciones.py`).
 *
 * «Desde lo pagado» resuelve el caso del acceso: hay transferencia y no hay
 * preliquidación. El servidor busca el céntimo que reproduce exactamente lo pagado y
 * dice si no lo encuentra, en vez de presentar como exacta una base que no cuadra.
 *
 * ⚠ **Con dólares y tasa de emisión, los bolívares no se teclean**: el campo muestra
 * lo que contestó el servidor (`montos`, alineado con las líneas) y se bloquea. La
 * conversión renglón a renglón es suya; aquí no se multiplica nada.
 */
export function LineasRetencion({
  datos,
  onCambio,
  montos = [],
}: {
  datos: BorradorRetencion
  onCambio: (datos: BorradorRetencion) => void
  /** Bolívares a emisión de cada línea según el servidor; `undefined` si no hay. */
  montos?: (string | undefined)[]
}) {
  const hayTasa = normalizarDecimal(datos.tasa_cambio) !== null
  const [pagado, setPagado] = useState("")
  const ponerLinea = (i: number, cambio: Partial<LineaRetencion>) =>
    onCambio({
      ...datos,
      lineas: datos.lineas.map((l, j) => (j === i ? { ...l, ...cambio } : l)),
    })

  const desdeNeto = useMutation({
    mutationFn: () =>
      api.post<DesdeNeto>("/api/v1/retenciones-provisionales/desde-neto", {
        neto: aDecimal(pagado),
        alicuota_iva: aDecimal(datos.alicuota_iva),
        porcentaje_ret_iva: aDecimal(datos.porcentaje_ret_iva),
        porcentaje_ret_islr: aDecimal(datos.porcentaje_ret_islr),
        sustraendo_islr: aDecimal(datos.sustraendo_islr || "0"),
      }),
    onSuccess: (r) => {
      const linea = { ...lineaEnBlanco(), descripcion: "Servicio", monto: r.base }
      // Una sola línea vacía es el formulario recién abierto: se sustituye.
      const vacias = datos.lineas.every((l) => l.monto.trim() === "" && l.monto_usd.trim() === "")
      onCambio({ ...datos, lineas: vacias ? [linea] : [...datos.lineas, linea] })
    },
  })

  return (
    <fieldset className="superficie grid gap-3 p-4">
      <legend className="rotulo px-1">Servicios</legend>
      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        Base sin IVA. En dólares, con la tasa de emisión, los bolívares salen solos; si no,
        escríbelos. Marca en cada servicio qué retenciones lleva.
      </p>

      <ul className="grid gap-3">
        {datos.lineas.map((linea, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: las líneas no tienen identidad propia
          <li key={i} className="grid gap-2 border-b pb-3" style={{ borderColor: "var(--border)" }}>
            <div className="flex gap-2">
              <input
                className="campo h-9 min-w-0 flex-1 text-sm"
                placeholder="Descripción del servicio"
                aria-label={`Descripción del servicio ${i + 1}`}
                value={linea.descripcion}
                maxLength={200}
                onChange={(e) => ponerLinea(i, { descripcion: e.target.value })}
              />
              <button
                type="button"
                className="boton-secundario grid h-9 w-9 shrink-0 place-items-center"
                aria-label={`Quitar el servicio ${i + 1}`}
                disabled={datos.lineas.length === 1}
                onClick={() =>
                  onCambio({ ...datos, lineas: datos.lineas.filter((_, j) => j !== i) })
                }
              >
                <Trash2 size={16} aria-hidden={true} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                className="campo tabular-nums h-9 w-full min-w-0 text-right text-sm"
                placeholder="USD"
                inputMode="decimal"
                aria-label={`Base en dólares del servicio ${i + 1}`}
                value={linea.monto_usd}
                onChange={(e) => ponerLinea(i, { monto_usd: e.target.value })}
              />
              {hayTasa && normalizarDecimal(linea.monto_usd) !== null ? (
                <input
                  className="campo tabular-nums h-9 w-full min-w-0 text-right text-sm"
                  readOnly={true}
                  aria-label={`Base en bolívares del servicio ${i + 1}, a la tasa de emisión`}
                  title="Dólares × tasa de emisión, calculado por el servidor"
                  value={montos[i] === undefined ? "…" : monto(montos[i] ?? "0")}
                />
              ) : (
                <input
                  className="campo tabular-nums h-9 w-full min-w-0 text-right text-sm"
                  placeholder="Bs"
                  inputMode="decimal"
                  aria-label={`Base en bolívares del servicio ${i + 1}`}
                  value={linea.monto}
                  onChange={(e) => ponerLinea(i, { monto: e.target.value })}
                />
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {MARCAS.map(([campo, rotulo, ayuda]) => {
                // Sin IVA no hay IVA que retener: la casilla se apaga, no se esconde.
                const apagada = campo === "retiene_iva" && linea.exento_iva
                return (
                  <label
                    key={campo}
                    className="flex min-h-6 items-center gap-1.5 text-xs"
                    title={ayuda}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={linea[campo] && !apagada}
                      disabled={apagada}
                      onChange={(e) => ponerLinea(i, { [campo]: e.target.checked })}
                    />
                    {rotulo}
                  </label>
                )
              })}
            </div>
          </li>
        ))}
      </ul>

      <Boton
        className="justify-self-start"
        onClick={() => onCambio({ ...datos, lineas: [...datos.lineas, lineaEnBlanco()] })}
      >
        <Plus size={15} aria-hidden={true} />
        Añadir servicio
      </Boton>

      <div className="grid gap-2 border-t pt-3" style={{ borderColor: "var(--border)" }}>
        <p className="text-xs font-medium">¿Sólo tienes lo que se pagó?</p>
        <div className="flex gap-2">
          <input
            className="campo tabular-nums h-9 min-w-0 flex-1 text-sm"
            placeholder="Monto pagado en Bs"
            inputMode="decimal"
            aria-label="Monto pagado en bolívares"
            value={pagado}
            onChange={(e) => setPagado(e.target.value)}
          />
          <Boton
            disabled={pagado.trim() === "" || desdeNeto.isPending}
            onClick={() => desdeNeto.mutate()}
          >
            <Calculator size={15} aria-hidden={true} />
            Sacar la base
          </Boton>
        </div>
        {desdeNeto.data && (
          <p className="text-xs" role="status">
            Base <span className="tabular-nums font-medium">{monto(desdeNeto.data.base)}</span>
            {desdeNeto.data.exacto
              ? " · reproduce el pago al céntimo. Añadida como servicio gravado con las dos retenciones."
              : ` · ningún céntimo da el pago exacto (queda en ${monto(desdeNeto.data.calculo.neto)}): revisa porcentajes o tasa.`}
          </p>
        )}
        {desdeNeto.error && (
          <p className="text-xs" role="alert" style={{ color: "var(--destructive)" }}>
            {desdeNeto.error.message}
          </p>
        )}
      </div>
    </fieldset>
  )
}
